<template>
  <div>
    <banner
      v-if="shouldShowMinTrialsBanner"
      :message="$t('insights.chart.hideBelowMinTrialsLimitation')"
    />
    <q-layout
      view="lHr lpR lFr"
      container
      :class="{
        'chart-container': !hideOnNoData || !noData,
        'chart-container--with-status-headers': hasVisibleStatusHeaders,
      }"
    >
      <q-header
        v-if="!hideHeader"
        class="bg-transparent"
      >
        <q-toolbar class="bg-transparent row">
          <q-item class="col">
            <q-item-section>
              <q-item-label class="text-dark row">
                <q-input
                  v-if="isEditingChartName"
                  ref="chartName"
                  :model-value="editChartName"
                  :label="$t('insights.editFavorite.inputNameLabel')"
                  lazy-rules
                  autofocus
                  class="full-width"
                  @update:model-value="onInputChartName"
                  @keydown.enter="isEditingChartName = false"
                  @blur="isEditingChartName = false"
                />
                <span
                  v-else-if="!hideChartName"
                  class="items-center"
                  @click="onEditChartName"
                >
                  {{ displayChartName }}
                  <q-btn
                    v-if="!disableChartNameEdit"
                    color="grey-5"
                    flat
                    size="sm"
                    icon="edit"
                    @click="onEditChartName"
                  />
                </span>
              </q-item-label>
              <q-item-label
                v-if="!hideDate"
                caption
              >
                {{ getSelectedPeriodLabel(dateRange, hasDateRangeDimensions) }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-btn
            v-show="!hideExport"
            flat
            round
            dense
            :disable="isLoadingData"
            icon="ios_share"
            color="primary-variant"
          >
            <q-menu>
              <q-list>
                <q-item
                  v-for="exportFormat in filteredExportFormats"
                  :key="exportFormat.type"
                  v-close-popup
                  clickable
                  @click="onExport(exportFormat.type)"
                >
                  <q-item-section avatar>
                    <q-icon
                      :name="exportFormat.icon"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section>
                    {{ exportFormat.label }}
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
          <q-btn
            v-if="!hideFavorite"
            flat
            round
            dense
            :icon="favoriteIcon"
            color="primary-variant"
            @click="onFavorite"
          >
            <q-tooltip
              v-if="favoriteTooltip"
            >
              {{ favoriteTooltip }}
            </q-tooltip>
          </q-btn>
          <q-btn
            v-if="!hideSettingsButton"
            flat
            round
            dense
            icon="tune"
            color="primary-variant"
            @click="showSettingsToggle = !showSettingsToggle"
          />
        </q-toolbar>
      </q-header>
      <q-drawer
        v-if="!hideOnNoData || !noData"
        :model-value="showSettingsToggle || forceShowSettings"
        side="right"
        bordered
        :width="250"
        :breakpoint="400"
        @update:model-value="showSettingsToggle = $event"
      >
        <q-toolbar class="bg-white row">
          <q-item class="col">
            <q-item-section>
              <q-item-label class="text-dark">
                {{ $t('insights.insightsComponent.textToolbarItemSettings') }}
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-btn
            v-if="showReset"
            flat
            round
            dense
            icon="restart_alt"
            color="primary-variant"
            @click="onResetChartSettings"
          >
            <q-tooltip>
              {{ $t('insights.chart.tooltipReset') }}
            </q-tooltip>
          </q-btn>
          <q-btn
            flat
            round
            dense
            icon="settings"
            color="primary-variant"
            @click="onEditChartSettings"
          />
          <q-btn
            v-if="!forceShowSettings"
            flat
            round
            dense
            icon="close"
            color="primary-variant"
            @click="showSettingsToggle = !showSettingsToggle"
          />
        </q-toolbar>
        <div class="q-px-sm">
          <chart-configurator
            :model-value="dataSeriesSettings"
            :users-func="getFilterUsers"
            :enable-min-trials-filter="enableMinTrialsFilter"
            :hide-annotations="hideAnnotations"
            :hide-filter="hideFilter"
            :chart-phase-line-scopes="chartPhaseLineScopes"
            :init-program-id="initProgramId"
            :init-draft-program-targets="initDraftProgramTargets"
            :filter-data-source-ids="filterDataSourceIds"
            @update:model-value="onChangeDataSeriesSettings"
            @refresh-phase-lines="onRefreshChartPhaseLines"
          >
            <chart-date-range-selector
              v-if="hasDateRange"
              :model-value="dateRange"
              :show-inherited-period="showInheritedPeriod"
              :dimensions="dateRangeDimensions"
              @update:model-value="onChangeDateRange"
            />
          </chart-configurator>
        </div>
      </q-drawer>
      <q-page-container>
        <q-page>
          <div
            v-if="isLoadingData"
            class="absolute full-width full-height text-center dimmed column justify-center z-max"
          >
            <q-spinner
              size="xl"
              class="self-center"
            />
          </div>
          <chart
            ref="chart"
            :data-series="chartDataSeriesWithPhaseLines"
            :use-phase-line-intervals="usePhaseLineIntervals"
            :data="chartData"
            :export-columns="exportColumns"
            :export-formatters="exportFormatters"
            :title="displayChartName"
            :show-title="embedChartTitle"
            :phase-lines="selectedPhaseLines"
            :split-x-axis="splitXAxis"
            :theme="theme"
            :chart-settings="chartSettings"
            :hide-legend="hideLegend"
            :analysis="analysisSettings"
            :loading="isLoadingData"
            :no-animation="noAnimation"
            @refreshed="onChartLoadDone"
            @loading="onLoading"
          />
        </q-page>
      </q-page-container>
    </q-layout>
    <insights-table
      v-if="!hideDataTable"
      :data-series-settings="tableDataSeriesSettings"
      :enable-min-trials-filter="enableMinTrialsFilter"
      :table-data="chartData"
      :series="chartDataSeries"
      :loading="isLoadingData"
      class="q-mt-md"
    />
  </div>
</template>
<script>
import { callFirebaseFunction } from 'boot/firebase';
import Chart from 'components/Insights/Chart';
import ChartConfigurator from 'components/Insights/ChartConfigurator';
import ChartDateRangeSelector from 'components/Insights/ChartDateRangeSelector';
import EditChartSettings from 'components/Insights/EditChartSettings';
import InsightsTable from 'components/Insights/InsightsTable';
import Banner from 'components/UI/Banner';
import { Timestamp } from 'firebase/firestore';
import {
  chain,
  cloneDeep,
  difference,
  filter,
  find,
  findIndex,
  flatMap,
  get,
  has,
  includes,
  indexOf,
  intersection,
  isEmpty,
  isEqual,
  isFunction,
  isNumber,
  isUndefined,
  keyBy,
  last,
  map,
  omit,
  orderBy,
  padStart,
  range,
  reduce,
  reject,
  set,
  size,
  sortBy,
  sumBy,
  unionWith,
  uniqBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import { AGGREGATORS } from 'src/constants/chart';
import ChartPhaseLinesDB from 'src/fb/chartPhaseLines-db';
import Insights from 'src/mixins/Insights';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { programBehaviourData } from 'src/store/programBehaviourData';
import { programsTargets } from 'src/store/programsTargets';
import { promptLevels } from 'src/store/promptLevels';
import { getClientUsers } from 'src/utils/OfflineAccessUtils';
import zlib from 'zlib';

export default {
  name: 'InsightsGraph',

  components: {
    Banner,
    Chart,
    ChartConfigurator,
    ChartDateRangeSelector,
    InsightsTable,
  },

  mixins: [
    Insights,
    Interactions,
    Targets,
  ],

  props: {

    graphInfo: {
      type: Object,
      required: true,
    },

    disableChartNameEdit: {
      type: Boolean,
      default: false,
    }, // disableChartNameEdit

    initProgramId: {
      type: String,
      default: null,
    },

    initDraftProgramTargets: {
      type: Array,
      default: () => [],
    },

    enableMinTrialsFilter: {
      type: Boolean,
      default: false,
    },

    hideOnNoData: {
      type: Boolean,
      default: false,
    },

    hideHeader: {
      type: Boolean,
      default: false,
    },

    hideFavorite: {
      type: Boolean,
      default: false,
    }, // hideFavorite

    hideAnnotations: {
      type: Boolean,
      default: false,
    },

    hideLegend: {
      type: Boolean,
      default: false,
    },

    favoriteIcon: {
      type: String,
      default: 'favorite_outline',
    }, // favoriteIcon

    favoriteTooltip: {
      type: String,
      default: null,
    }, // favoriteTooltip

    chartName: {
      type: String,
      default: '',
    }, // chartName()

    hideChartName: {
      type: Boolean,
      default: false,
    },

    embedChartTitle: {
      type: Boolean,
      default: false,
    },

    hideExport: {
      type: Boolean,
      default: false,
    },

    hideDate: {
      type: Boolean,
      default: false,
    },

    hideSettings: {
      type: Boolean,
      default: false,
    }, // hideSettings()

    hideSettingsButton: {
      type: Boolean,
      default: false,
    }, // hideSettingsButton()

    hideDataTable: {
      type: Boolean,
      default: false,
    },

    showInheritedPeriod: {
      type: Boolean,
      default: false,
    },

    showReset: {
      type: Boolean,
      default: false,
    },

    clientId: {
      type: String,
      default: null,
    },

    hideFilter: {
      type: Boolean,
      default: false,
    },

    forceShowSettings: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    filterDataSourceIds: {
      type: Array,
      default: () => [],
    }, // filterDataSourceIds

    noAnimation: {
      type: Boolean,
      default: false,
    },

  }, // props

  emits: [
    'loaded',
    'update-display-chart-name',
    'input-chart-name',
    'change-date-range',
    'change-data-series-settings',
    'reset-chart-settings',
    'favorite',
    'chart-loaded',
  ],

  setup() {
    const { getLocaleDateString, formatDate, startOfDate } = useTime();
    return { getLocaleDateString, formatDate, startOfDate };
  },

  data() {
    return {
      dateRange: {},
      dataSeriesSettings: {},

      chartData: [],
      rawChartData: [],
      chartDataSeries: [],
      exportColumns: {},
      exportFormatters: {},
      showSettingsToggle: true,
      isEditingChartName: false,
      editChartName: '',

      chartPhaseLineScopes: [],
      statusPhaseLines: [],
      shouldRefreshChartAfterPhaseLinesLoaded: false,

      tableDataSeriesSettings: {
        dataSource: '',
        preloadFilter: null,
        postloadFilter: {},

        dataSeriesPreset: null,
        dataProperty: null,
        aggregator: null,
        chartType: null,

        groupByDataProperty: null,
      },

      userNameCache: null,

      isLoading: false,
      isLoadingPhaseLines: false,
      loadingInfo: null,
      prevRefreshGraphInfo: {},

      chartDataSeriesWithPhaseLines: [],

      colorThemes: {
        default: [
          '#03A9F4', '#f44336', '#673AB7',
          '#4CAF50', '#FFEB3B', '#FF5722', '#607D8B',
          '#E91E63', '#3F51B5', '#00BCD4', '#8BC34A',
          '#FFC107', '#795548', '#9C27B0', '#2196F3',
          '#009688', '#CDDC39', '#FF9800', '#9E9E9E',
        ],
        bw: [
          // 100% opacity
          '#0F0F0F', '#323232', '#5a5a5a', '#828282',
          '#afafaf', '#dcdcdc', '#ebebeb',
          // 50% opacity
          '#0F0F0F80', '#5A5A5A7F', '#AFAFAF80', '#E6E6E699',
        ],
      },
      bulletThemes: {
        default: null,
        bw: [
          { type: 'Circle', fill: '#FFF', stroke: null },
          { type: 'Triangle', direction: 'top', fill: null, stroke: '#FFF' },
          { type: 'Rectangle', fill: '#FFF', stroke: null },
          { type: 'Circle', fill: null, stroke: '#FFF' },
          { type: 'Triangle', direction: 'bottom', fill: null, stroke: '#FFF' },
          { type: 'Rectangle', fill: null, stroke: '#FFF' },
          { type: 'Triangle', direction: 'left', fill: null, stroke: '#FFF' },
          { type: 'Triangle', direction: 'right', fill: null, stroke: '#FFF' },
          { type: 'Triangle', direction: 'bottom', fill: '#FFF', stroke: null },
        ],
      },
      exportFormats: [
        { type: 'png', label: this.$t('insights.chart.export.image'), icon: 'fas fa-file-image' },
        { type: 'pdf', label: this.$t('insights.chart.export.pdf'), icon: 'fas fa-file-pdf' },
        { type: 'pdfdata', label: this.$t('insights.chart.export.pdfDataTable'), icon: 'fas fa-table' },
        { type: 'xlsx', label: this.$t('insights.chart.export.excel'), icon: 'fas fa-file-excel' },
        { type: 'print', label: this.$t('insights.chart.export.print'), icon: 'print' },
      ],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(programBehaviourData, ['programBehaviourGroups', 'programBehaviourGroupItems']),
    ...mapState(promptLevels, ['promptLevelTemplates']),
    ...mapState(network, ['offlineMode']),

    canRender() {
      return !!(this.dataSeriesSettings.dataSource
        && this.dataSeriesSettings.dataProperty
        && this.dataSeriesSettings.aggregator
        && this.dataSeriesSettings.chartType
        && this.clientId);
    }, // canRender()

    noData() {
      return isEmpty(this.chartData);
    }, // noData()

    isLoadingData() {
      return this.loading || this.isLoading || this.isLoadingPhaseLines;
    }, // isLoadingData()

    hasDateRange() {
      return get(this.getCurrentDataSource(), 'hasDateRange', false);
    }, // hasDateRange()

    hasDateRangeDimensions() {
      return !chain(this.getCurrentDataSource()).get('dateRangeDimensions').isEmpty().value();
    }, // hasDateRange()

    behaviorIncidentProgram() {
      return chain(this.programs).filter({ type: 'behaviour' }).head().value();
    }, // behaviorIncidentProgram()

    dateRangeDimensions() {
      return get(this.getCurrentDataSource(), 'dateRangeDimensions');
    }, // dateRangeDimensions()

    filteredExportFormats() {
      return this.$q.platform.is.capacitor
        ? reject(this.exportFormats, { type: 'print' })
        : this.exportFormats;
    }, // nativeExportFormats()

    displayChartName() {
      return this.editChartName || this.defaultChartName;
    }, // displayChartName()

    defaultChartName() {
      let favoriteName = chain(this.getCurrentDataSource())
        .get('seriesPresets')
        .find({ id: this.dataSeriesSettings.dataSeriesPreset })
        .get('label', '-')
        .value();

      if (this.dataSeriesSettings.groupByDataProperty) {
        const groupByLabel = chain(this.getCurrentDataSource())
          .get('groupByOptions')
          .find({ value: this.dataSeriesSettings.groupByDataProperty })
          .get('label')
          .value();
        if (groupByLabel) {
          favoriteName = favoriteName
            .concat(' per ')
            .concat(groupByLabel);
        }
      }
      return favoriteName;
    }, // defaultChartName()

    theme() {
      return {
        colors: this.colorThemes[get(this.dataSeriesSettings, ['chartSettings', 'theme'], 'default')],
        bullets: this.bulletThemes[get(this.dataSeriesSettings, ['chartSettings', 'theme'], 'default')],
      };
    }, // theme()

    chartSettings() {
      const baseSettings = get(this.dataSeriesSettings, 'chartSettings', null) || {};

      if (this.dataSeriesSettings?.dataSeriesPreset !== 'spAverageRating') {
        return baseSettings;
      }

      const activeTemplateIds = this.getActiveRatingTemplateIds();
      const { min, max } = this.getRatingScaleMinMax(activeTemplateIds);

      return {
        ...baseSettings,
        yAxisMin: 'custom',
        yAxisMax: 'custom',
        yAxisMinValue: min,
        yAxisMaxValue: max,
        yStrictMinMax: true,
      };
    }, // chartSettings()

    splitXAxis() {
      if (!this.dataSeriesSettings?.xAxisSplitBy) {
        return null;
      }
      return {
        field: this.dataSeriesSettings.xAxisSplitBy,
        format: (firstDataItem) => {
          if (firstDataItem?.date instanceof Date) {
            const dateFormat = this.dateFormats.find(
              ({ value }) => (value === this.dataSeriesSettings.chartSettings?.xAxisDateFormat),
            );
            if (this.dataSeriesSettings.chartSettings?.xAxisUseCustomDateFormat && dateFormat) {
              return this.formatDate(firstDataItem.date, dateFormat.luxon);
            }
            return this.getLocaleDateString(firstDataItem.date, {
              options: { day: 'numeric', month: 'short', year: '2-digit' },
            });
          }
          return null;
        },
      };
    }, // splitXAxis()

    selectedPhaseLines() {
      if ((this.dataSeriesSettings.xAxisType !== 'date')
        && (this.dataSeriesSettings.xAxisOrderBy !== 'date')) {
        return [];
      }

      const localPhaseLines = chain(this.dataSeriesSettings)
        .get('phaseLines', [])
        .filter((phaseLine) => (
          (get(phaseLine, 'scope', 'local') === 'local')
          && (chain(phaseLine).get(['startTimestamp', 'toDate']).isFunction().value())
          && (phaseLine.startTimestamp.toDate() >= this.dateRange.fromTimestamp)
          && (phaseLine.startTimestamp.toDate() <= this.dateRange.toTimestamp)
        ))
        .value();
      const scopedPhaseLines = chain(this.dataSeriesSettings)
        .get('phaseLines', [])
        .filter((phaseLineScope) => (
          ['global', 'program', 'target', 'subTarget'].includes(get(phaseLineScope, 'scope', 'local'))
          && phaseLineScope?.isSelected === true
        ))
        .flatMap((phaseLineScope) => {
          const scopeFilter = this.getPhaseLineScopeFilter(
            phaseLineScope.scope,
            phaseLineScope.program,
            phaseLineScope.target,
          );
          return chain(this.chartPhaseLineScopes)
            .find(scopeFilter)
            .get('chartPhaseLines', [])
            .value();
        })
        .filter((phaseLine) => (
          (chain(phaseLine).get(['startTimestamp', 'toDate']).isFunction().value())
          && (phaseLine.startTimestamp.toDate() >= this.dateRange.fromTimestamp)
          && (phaseLine.startTimestamp.toDate() <= this.dateRange.toTimestamp)
        ))
        .value();

      // Date axes group by day: snap the change to its day-start so same-day data lands on the
      // right side. Session (category) axes keep sessions distinct, so precise timestamps stay.
      const snapStatusPhaseLine = (phaseLine) => {
        if (this.dataSeriesSettings.xAxisType !== 'date') {
          return phaseLine;
        }
        const dayStart = this.startOfDate(phaseLine.startTimestamp.toDate(), 'day');
        return { ...phaseLine, startTimestamp: Timestamp.fromDate(dayStart) };
      };

      // Filter status phase lines to date range (only when toggled on)
      // Then remove phase lines that create sections with no data points
      let filteredStatusPhaseLines = get(this.dataSeriesSettings, 'analysis.programStatusPhaseLines') !== false
        ? (this.statusPhaseLines || [])
          .filter((phaseLine) => chain(phaseLine).get(['startTimestamp', 'toDate']).isFunction().value())
          .map(snapStatusPhaseLine)
          .filter((phaseLine) => (
            phaseLine.startTimestamp.toDate() >= this.dateRange.fromTimestamp
            && phaseLine.startTimestamp.toDate() <= this.dateRange.toTimestamp
          ))
        : [];

      // When the x-axis groups data into time buckets (day/week/month), collapse status
      // transitions that fall in the same bucket: they render at one x-position, so
      // keeping the intermediate ones only stacks overlapping headers. Keep the first
      // transition's position and initial-section label, but adopt the bucket's final
      // status as the label. Session/category axes and ungrouped date axes are untouched.
      const { xAxisType, xAxisTimeUnit } = this.dataSeriesSettings;
      const isBucketed = xAxisType === 'date' && !!xAxisTimeUnit;
      if (isBucketed && filteredStatusPhaseLines.length > 1) {
        const collapsed = [];
        filteredStatusPhaseLines.forEach((phaseLine) => {
          const bucketKey = this.startOfDate(phaseLine.startTimestamp, xAxisTimeUnit).getTime();
          const prev = collapsed[collapsed.length - 1];
          if (prev && prev.bucketKey === bucketKey) {
            prev.phaseLine = { ...prev.phaseLine, label: phaseLine.label };
          } else {
            collapsed.push({ bucketKey, phaseLine: { ...phaseLine } });
          }
        });
        filteredStatusPhaseLines = collapsed.map((entry) => entry.phaseLine);
      }

      // On bucketed charts (day/week/month), data points within a single bucket render
      // at one x-position. If every data point collapses into the same bucket, any
      // transition that happens inside that bucket isn't visible on the chart — drop
      // the phase lines entirely so we don't draw dividers/headers with nothing to
      // annotate (e.g. all sessions and all status changes on the same day in a daily
      // view).
      if (isBucketed && filteredStatusPhaseLines.length > 0 && this.chartData.length > 0) {
        const dataBucketKeys = new Set(
          this.chartData
            .map((d) => (d.date ? this.startOfDate(d.date, xAxisTimeUnit).getTime() : null))
            .filter((key) => key !== null),
        );
        if (dataBucketKeys.size < 2) {
          filteredStatusPhaseLines = [];
        }
      }

      if (filteredStatusPhaseLines.length > 0 && this.chartData.length > 0) {
        const dataDateTimes = this.chartData.map((d) => d.date?.getTime()).filter(Boolean);

        // The initial section (dateRange.fromTimestamp -> first transition) gets its
        // visibility decided independently from the first transition's own interval,
        // so the "hide empty sections" rule applies correctly to both.
        const originalInitialLabel = filteredStatusPhaseLines[0].initialStatusLabel;
        const initialSectionStart = this.dateRange.fromTimestamp.getTime();
        const initialSectionEnd = filteredStatusPhaseLines[0].startTimestamp.toDate().getTime();
        const initialSectionHasData = !!originalInitialLabel
          && dataDateTimes.some((dt) => dt >= initialSectionStart && dt < initialSectionEnd);

        filteredStatusPhaseLines = filteredStatusPhaseLines.filter((phaseLine, idx) => {
          const sectionStart = phaseLine.startTimestamp.toDate().getTime();
          const nextPhaseLine = filteredStatusPhaseLines[idx + 1];
          const sectionEnd = nextPhaseLine
            ? nextPhaseLine.startTimestamp.toDate().getTime()
            : Infinity;
          return dataDateTimes.some((dt) => dt >= sectionStart && dt < sectionEnd);
        });

        if (filteredStatusPhaseLines.length > 0) {
          const leader = filteredStatusPhaseLines[0];
          if (initialSectionHasData && leader.initialStatusLabel !== originalInitialLabel) {
            filteredStatusPhaseLines[0] = { ...leader, initialStatusLabel: originalInitialLabel };
          } else if (!initialSectionHasData && leader.initialStatusLabel) {
            filteredStatusPhaseLines[0] = { ...leader, initialStatusLabel: undefined };
          }

          // A status phase line only conveys a transition when there is data on both sides
          // of it. Count data-backed sections: one per surviving phase line (its post-
          // transition section has data, having survived the filter above) plus the initial
          // section when it has data. With fewer than two there is no visible transition to
          // annotate — e.g. every change happened within one bucket, or data only ever
          // existed in a single status — so drop the headers/dividers entirely.
          const dataBackedSections = filteredStatusPhaseLines.length
            + (filteredStatusPhaseLines[0].initialStatusLabel ? 1 : 0);
          if (dataBackedSections < 2) {
            filteredStatusPhaseLines = [];
          }
        }
      }

      const phaseLines = chain(localPhaseLines)
        .concat(scopedPhaseLines)
        .concat(filteredStatusPhaseLines)
        .orderBy((pl) => pl.startTimestamp.toDate())
        .value();

      const xAxisProperties = ['cumulativeSession', 'cumulativeTrial', 'sessionTrial'];
      if (xAxisProperties.includes(this.dataSeriesSettings.xAxisDataProperty)) {
        // Add session index to phaselines if xAxis is session
        return chain(phaseLines)
          .map((phaseLine) => {
            const sessionIndex = this.chartData.findIndex(
              (data) => {
                const isValidSession = data[this.dataSeriesSettings.xAxisDataProperty] && data.sessionDate;
                return isValidSession && (data.date.getTime() > phaseLine.startTimestamp.toDate().getTime());
              },
            );
            if (sessionIndex === -1) {
              return {
                ...phaseLine,
                category: chain(this.chartData)
                  .reject({ hidden: true })
                  .maxBy(this.dataSeriesSettings.xAxisDataProperty)
                  .get('sessionDate')
                  .value(),
                isAfterData: true,
              };
            }
            return { ...phaseLine, category: get(this.chartData[sessionIndex], 'sessionDate') };
          })
          .value();
      }
      return phaseLines;
    }, // selectedPhaseLines()

    phaseLineIntervals() {
      if (isEmpty(this.dateRange)) {
        return [];
      }
      return chain(this.selectedPhaseLines)
        .concat({ startTimestamp: Timestamp.fromDate(this.dateRange.toTimestamp) })
        .map((phaseLine, phaseLineIdx) => ({
          fromTimestamp: phaseLineIdx === 0
            ? this.dateRange.fromTimestamp
            : this.selectedPhaseLines[phaseLineIdx - 1].startTimestamp.toDate(),
          toTimestamp: phaseLine.startTimestamp.toDate(),
        }))
        .value();
    }, // phaseLineIntervals()

    usePhaseLineIntervals() {
      return size(this.phaseLineIntervals) > 1
        && ((this.dataSeriesSettings?.xAxisType === 'date')
          || (this.dataSeriesSettings?.xAxisOrderBy === 'date'));
    },

    analysisSettings() {
      return get(this.dataSeriesSettings, 'analysis', {});
    }, // analysisSettings

    hideBelowMinTrials() {
      return this.dataSeriesSettings.additionalSettings?.hideBelowMinTrials ?? false;
    }, // hideBelowMinTrials()

    shouldShowMinTrialsBanner() {
      return this.enableMinTrialsFilter && this.hideBelowMinTrials;
    }, // shouldShowMinTrialsBanner()

    hasVisibleStatusHeaders() {
      return this.selectedPhaseLines.some((pl) => pl.isStatusTransition);
    }, // hasVisibleStatusHeaders()

  },

  watch: {

    isLoadingData: {
      immediate: false,
      handler(currentState, prevState) {
        if ((prevState === true) && (currentState === false)) {
          this.$emit('loaded');
        }
      },
    },

    chartDataSeries() {
      if (!this.usePhaseLineIntervals) {
        this.chartDataSeriesWithPhaseLines = this.chartDataSeries;
      } else {
        this.chartDataSeriesWithPhaseLines = flatMap(
          this.chartDataSeries,
          (chartDataSeries, seriesIdx) => (map(range(0, this.phaseLineIntervals.length), (phaseLineIdx) => ({
            ...chartDataSeries,
            phaseLineIdx,
            seriesIdx,
            seriesColumn: `${chartDataSeries.seriesColumn}_${phaseLineIdx}`,
          }))
          ),
        );
      }
    },

    graphInfo: {
      immediate: true,
      deep: true,
      async handler(graphInfo) {
        if (isEqual(get(graphInfo, 'dateRange'), this.dateRange)
         && isEqual(get(graphInfo, 'dataSeriesSettings'), this.dataSeriesSettings)) {
          return;
        }

        if (chain(graphInfo).get('dateRange').isEmpty().value()) {
          return;
        }

        this.dateRange = get(graphInfo, 'dateRange', {});
        this.dataSeriesSettings = get(graphInfo, 'dataSeriesSettings', {});

        const programIdsPath = ['postloadFilter', 'programs', 'programId'];
        const programIds = get(this.dataSeriesSettings, programIdsPath, []);

        // If the length of the programId array is 1 and the ID in this array does not match any of the IDs
        // from this client's program list, then it means that the programId array contains an incorrect ID.
        // Therefore, the ID of the current program needs to be added.
        if (this.initProgramId && programIds.length === 1 && !this.isProgramIdInPrograms(programIds[0])) {
          const updatedDataSeriesSettings = cloneDeep(this.dataSeriesSettings);
          set(updatedDataSeriesSettings, programIdsPath, [...programIds, this.initProgramId]);

          this.dataSeriesSettings = updatedDataSeriesSettings;
        }

        if (!get(this.dataSeriesSettings, 'dataSource')) {
          return;
        }
        const refreshGraphInfo = {
          ...chain(graphInfo).get('dateRange').pick(['fromTimestamp', 'toTimestamp', 'periodDimension']).value(),
          ...chain(graphInfo)
            .get('dataSeriesSettings')
            .pick(['aggregator', 'dataProperty', 'dataSource', 'groupByDataProperty', 'groupBySeriesLabelFrom',
              'groupBySeriesOrderBy', 'postloadFilter', 'xAxis', 'xAxisAggregateBy', 'xAxisDataProperty',
              'xAxisTimeUnit', 'xAxisType', 'frequencyDivisor', 'additionalSettings.hideBelowMinTrials'])
            .cloneDeep()
            .value(),
        };
        if (!isEqual(refreshGraphInfo, this.prevRefreshGraphInfo)) {
          await this.refreshData();
        }

        // We need to wait until phase lines are loaded before we can refresh the chart
        // in order them to be displayed straight away
        if (this.isLoadingPhaseLines) {
          this.shouldRefreshChartAfterPhaseLinesLoaded = true;
        } else {
          await this.refreshChart();
        }
        this.prevRefreshGraphInfo = refreshGraphInfo;
      },
    },

    isLoadingPhaseLines: {
      handler(newVal, oldVal) {
        if (this.shouldRefreshChartAfterPhaseLinesLoaded && (newVal === false) && oldVal) {
          this.shouldRefreshChartAfterPhaseLinesLoaded = false;
          this.refreshChart();
        }
      },
    },

    chartName: {
      immediate: true,
      deep: false,
      async handler(newChartName) {
        this.editChartName = newChartName;
      },
    },

    displayChartName: {
      immediate: true,
      deep: false,
      handler(newDisplayChartName) {
        this.$emit('update-display-chart-name', newDisplayChartName);
      },
    },

    hideSettings: {
      immediate: true,
      deep: false,
      handler(newHideSettings) {
        this.showSettingsToggle = !newHideSettings;
      },
    },
  },

  async created() {
    const selectedPhaseLineScopes = chain(this.dataSeriesSettings)
      .get('phaseLines', [])
      .filter((phaseLine) => (phaseLine.isSelected && (phaseLine.scope !== 'local')))
      .value();

    this.reloadChartPhaseLineScopes(selectedPhaseLineScopes);
    await this.loadProgramBehaviourDataGroups({
      programId: get(this.behaviorIncidentProgram, 'id'),
      loadItems: true,
      rejectDeleted: false,
    });
  }, // created()

  methods: {
    ...mapActions(programsTargets, ['getProgramTarget', 'getAllProgramTargets']),
    ...mapActions(programBehaviourData, ['loadProgramBehaviourDataGroups']),
    ...mapActions(promptLevels, ['loadPromptLevelTemplates']),

    isProgramIdInPrograms(programId) {
      return this.programs.some(({ id }) => id === programId);
    }, // isProgramIdInPrograms()

    getCurrentDataSource() {
      return find(this.dSources, { id: this.dataSeriesSettings.dataSource });
    },

    getExportImage() {
      return this.$refs.chart.getExportedChart('png');
    }, // getExportImage()

    onLoading(loadingState) {
      this.isLoading = loadingState;
    }, // onLoading()

    onEditChartName() {
      if (this.disableChartNameEdit) {
        return;
      }
      this.isEditingChartName = true;

      this.$nextTick(() => {
        if (this.$refs.chartName) {
          this.$refs.chartName.focus();
        }
      });
    }, // onEditChartName()

    onInputChartName(chartName) {
      this.$emit('input-chart-name', chartName);
    }, // onInputChartName()

    onExport(exportFormatType) {
      this.$refs.chart.exportChart(exportFormatType);
    }, // onExport

    onChangeDateRange(newDateRange) {
      this.$emit('change-date-range', newDateRange);
    }, // onChangeDateRange()

    async onChangeDataSeriesSettings(newDataSeriesSettings) {
      if (!chain(newDataSeriesSettings)
        .get('phaseLines', [])
        .isEqual(get(this.dataSeriesSettings, 'phaseLines', []))
        .value()) {
        await this.reloadChartPhaseLineScopes(newDataSeriesSettings.phaseLines);
      }

      this.$emit('change-data-series-settings', newDataSeriesSettings);
    }, // onChangeDataSeriesSettings()

    async onRefreshChartPhaseLines(chartPhaseLineScopes = null) {
      const refreshAll = true;
      const scopes = refreshAll
        ? get(this.dataSeriesSettings, 'phaseLines', [])
        : chartPhaseLineScopes;
      await this.reloadChartPhaseLineScopes(scopes, refreshAll, true);
      this.refreshChart();
    }, // onRefreshChartPhaseLines()

    onFavorite() {
      this.$emit('favorite');
    }, // onFavorite()

    onResetChartSettings() {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('insights.chart.confirmResetSettings'),
        () => {
          this.$emit('reset-chart-settings');
        },
      );
    }, // onResetChartSettings()

    onEditChartSettings() {
      const chartSettings = get(this.dataSeriesSettings, 'chartSettings', {});

      // Show dialog and receive new settings
      this.openComponentDialog(EditChartSettings, {
        chartSettings,
        dataSeriesSettings: this.dataSeriesSettings,
      }, (newChartSettings) => {
        if (isEqual(chartSettings, newChartSettings)) {
          return;
        }

        const newDataSeriesSettings = {
          ...this.dataSeriesSettings,
          chartSettings: newChartSettings,
        };
        this.$emit('change-data-series-settings', newDataSeriesSettings);
      });
    }, // onEditChartSettings()

    onChartLoadDone(hasData) {
      if (!hasData && !this.hideSettings) {
        this.showSettingsToggle = true;
      }

      this.$emit('chart-loaded');
    }, // onChartLoadDone()

    async reloadClientUsers() {
      if (this.userNameCache !== null) {
        return;
      }

      let clientUsersResult;

      if (!this.offlineMode) {
        clientUsersResult = await callFirebaseFunction('getClientUsers', {
          clinicId: this.userDetails.clinic,
          clientId: this.clientId,
          includeSelf: true,
          includeInactive: true,
        });
      } else {
        clientUsersResult = await getClientUsers({
          clinicId: this.userDetails.clinic,
          clientId: this.clientId,
          includeSelf: true,
          userId: this.userDetails.userId,
          includeInactive: true,
        });
      }

      this.userNameCache = chain(clientUsersResult)
        .get('data')
        .map((clientUser) => ([clientUser.user, clientUser.userName]))
        .fromPairs()
        .value();
    }, // reloadClientUsers()

    async getFilterUsers() {
      await this.reloadClientUsers();
      return chain(this.userNameCache)
        .map((userName, userId) => ({ userId, userName }))
        .sortBy('userName')
        .value();
    }, // filterUsers()

    async getDataSeriesLabel(groupByDataProperty, firstDataItem, groupBySeriesLabelFrom) {
      if (groupBySeriesLabelFrom === null) {
        return firstDataItem[groupByDataProperty];
      }

      // Get data series labels
      if (groupByDataProperty === 'programId') {
        return chain(this.programs).find({ id: firstDataItem.programId }).get('programName').value();
      }
      if (groupByDataProperty === 'targetId') {
        return get(await this.getProgramTarget({
          program: firstDataItem.programId,
          target: firstDataItem.targetId,
          preventDuplicates: true,
        }), 'label', null)
          || `${firstDataItem.targetLabel} (${this.$t('common.deleted')})`
          || '';
      }
      if (groupByDataProperty === 'promptLevelId') {
        return this.getPromptLevelProp(
          firstDataItem.promptLevelTemplateId,
          firstDataItem.promptLevelId,
          'trialOutcome',
          '?',
        );
      }
      if (groupByDataProperty === 'currentPromptLevelId') {
        return this.getPromptLevelProp(
          firstDataItem.promptLevelTemplateId,
          firstDataItem.currentPromptLevelId,
          'trialOutcome',
          '?',
        );
      }
      if (groupByDataProperty === 'mode') {
        const programType = chain(this.programs).find({ id: firstDataItem.programId }).get('type').value();
        return this.getModeLabel(programType, firstDataItem.mode);
      }
      if (groupByDataProperty === 'userId') {
        return this.getUserName(firstDataItem.userId);
      }
      if (groupBySeriesLabelFrom === 'behaviourItemLabel') {
        const behaviorGroup = groupByDataProperty.slice(3);
        return get(
          this.programBehaviourGroupItems,
          [behaviorGroup, get(firstDataItem, groupByDataProperty), 'label'],
          '',
        );
      }
      return '?';
    }, // getDataSeriesLabel()

    async getDataSeriesColor(groupByDataProperty, firstDataItem) {
      if (groupByDataProperty === 'promptLevelId') {
        const promptLevel = chain(this.promptLevelTemplates)
          .get([firstDataItem.promptLevelTemplateId, 'promptLevels'])
          .find({ id: firstDataItem.promptLevelId })
          .value();
        return this.getPromptLevelColor(promptLevel);
      }
      if (groupByDataProperty === 'currentPromptLevelId') {
        const promptLevel = chain(this.promptLevelTemplates)
          .get([firstDataItem.promptLevelTemplateId, 'promptLevels'])
          .find({ id: firstDataItem.currentPromptLevelId })
          .value();
        return this.getPromptLevelColor(promptLevel);
      }
      return null;
    }, // getDataSeriesColor()

    async getProgramTargetOrder(programId, targetId, targetOrder = null, recurrence = 0) {
      const target = await this.getProgramTarget({
        program: programId,
        target: targetId,
      });
      if (!target?.order || (recurrence > 10)) {
        // Prevent infinite loop
        return targetOrder;
      }
      const targetOrderPadded = padStart(String(target.order), 4, '0');
      const newTargetOrder = targetOrder === null
        ? targetOrderPadded
        : `${targetOrderPadded}.${targetOrder}`;
      if (target.parentTarget) {
        return this.getProgramTargetOrder(programId, target.parentTarget, newTargetOrder, recurrence + 1);
      }
      return newTargetOrder;
    }, // getProgramTargetOrder()

    /**
    * Get order parameter based on how series is grouped
    */
    async getDataSeriesOrder(groupByOrderByProperty, firstDataItem, defaultValue) {
      if (firstDataItem[groupByOrderByProperty]) {
        return firstDataItem[groupByOrderByProperty];
      }

      // Get data series labels
      if (groupByOrderByProperty === 'targetOrder') {
        if (firstDataItem.targetOrder) {
          return firstDataItem.targetOrder;
        }
        return this.getProgramTargetOrder(firstDataItem.programId, firstDataItem.targetId);
      }
      if (groupByOrderByProperty === 'promptLevelId') {
        if (firstDataItem.promptLevelOrder) {
          return firstDataItem.promptLevelOrder;
        }
        return this.getPromptLevelProp(
          firstDataItem.promptLevelTemplateId,
          firstDataItem.promptLevelId,
          'order',
          '',
        );
      }
      if (groupByOrderByProperty === 'currentPromptLevelId') {
        return this.getPromptLevelProp(
          firstDataItem.promptLevelTemplateId,
          firstDataItem.promptLevelId,
          'order',
          null,
        );
      }
      if (groupByOrderByProperty === 'mode') {
        const programType = chain(this.programs).find({ id: firstDataItem.programId }).get('type').value();
        return this.getModeOrder(programType, firstDataItem.mode);
      }
      return defaultValue;
    }, // getDataSeriesOrder()

    async reloadData() {
      try {
        const newLoadingInfo = {
          dataSource: this.dataSeriesSettings.dataSource,
          dateRange: this.dateRange,
          preloadFilter: this.dataSeriesSettings.preloadFilter,
        };
        // Make sure we don't load the same data multiple times
        if (!isEqual(newLoadingInfo, this.loadingInfo)) {
          this.loadingInfo = newLoadingInfo;
        }
      } finally {
        this.loadingInfo = null;
      }
    }, // reloadData()

    /**
     * Generate an array of series based on the grouping specified in
     * data series settings. If data is not grouped then only one series is
     * returned.
     */
    async getDataSeries(dataSource, dataSeriesSettings) {
      let chartDataSeries = [];
      const groupByDataProperty = get(dataSeriesSettings, 'groupByDataProperty', null);
      const groupByOrderByProperty = get(dataSeriesSettings, 'groupBySeriesOrderBy') || groupByDataProperty;
      const groupBySeriesLabelFrom = get(dataSeriesSettings, 'groupBySeriesLabelFrom', '');
      const isGroupedByTarget = groupByDataProperty === 'targetId';

      if (['promptLevelId', 'currentPromptLevelId'].includes(groupByDataProperty)) {
        await this.loadPromptLevelTemplates();
      }

      if (groupByDataProperty === null) {
        const series = {
          ...omit(dataSeriesSettings, 'phaseLines'),
          seriesColumn: '_s',
        };
        chartDataSeries.push(series);
      } else {
        let chartDataSeriesActions = chain(dataSource)
          .filter((dataItems) => has(dataItems, groupByDataProperty));

        if (get(dataSeriesSettings, 'arrayGrouping', null)) {
          // ABC/BehaviorIncident data can contain data from multiple series
          // in a single row and thus we have to expand the data to get all series
          chartDataSeriesActions = chartDataSeriesActions
            .flatMap((dataItems) => chain(dataItems)
              .get(groupByDataProperty)
              .map((seriesId) => ({ ...dataItems, [groupByDataProperty]: seriesId }))
              .value());
        }

        chartDataSeriesActions = chartDataSeriesActions
          .groupBy(groupByDataProperty)
          .map(async (dataGroup, seriesId) => {
            const dataItem = isGroupedByTarget
              // If grouped by target, we need to find any data item that has `targetLabel` property
              // to be able get the label of the deleted targets as well
              ? dataGroup.find((item) => item[groupBySeriesLabelFrom]) || last(dataGroup)
              : last(dataGroup);

            let label = get(dataItem, groupBySeriesLabelFrom, null);
            // If it's grouped by target, we should try to get the target label from DB firstly to get current
            // name of the target if it exists as it can be different from the one in `dataItem`
            if (label === null || isGroupedByTarget) {
              label = await this.getDataSeriesLabel(groupByDataProperty, dataItem, groupBySeriesLabelFrom);
            }
            const color = await this.getDataSeriesColor(groupByDataProperty, dataItem);
            const order = await this.getDataSeriesOrder(groupByOrderByProperty, dataItem, label);

            return {
              ...omit(dataSeriesSettings, 'phaseLines'),
              seriesId,
              seriesColumn: `_s${seriesId}`,
              label,
              ...(color !== null) ? { color } : {},
              order,
            };
          })
          .value();
        chartDataSeries = await Promise.all(chartDataSeriesActions);
      }
      return chartDataSeries;
    }, // getDataSeries()

    async getGroupingDataProperties(groupByDataProperty, dataItem, groupedIndex) {
      switch (groupByDataProperty) {
        case 'sessionId':
          return {
            ...dataItem,
            session: groupedIndex,
            sessionDate: `[${this.getLocaleDateString(dataItem.date)}] ${groupedIndex}`,
            sessionDateOnly: this.formatDate(dataItem.date, 'yyyy-MM-dd'),
          };
        case 'cumulativeSession':
          return {
            ...dataItem,
            sessionDate: `[${this.getLocaleDateString(dataItem.date)}] ${dataItem.cumulativeSession}`,
            sessionDateOnly: this.formatDate(dataItem.date, 'yyyy-MM-dd'),
          };
        case 'sessionTrial':
          return {
            ...dataItem,
            sessionDate: `[${this.getLocaleDateString(dataItem.date)}] ${dataItem.sessionTrial}`,
            sessionTrial: `[${this.getLocaleDateString(dataItem.date)}] ${dataItem.sessionTrial}`,
          };
        case 'cumulativeTrial':
          return {
            ...dataItem,
            sessionDate: `[${this.getLocaleDateString(dataItem.date)}] ${dataItem.cumulativeTrial}`,
            cumulativeTrial: `[${this.getLocaleDateString(dataItem.date)}] ${dataItem.cumulativeTrial}`,
          };
        case 'incidentId':
          return {
            ...dataItem,
            incident: groupedIndex,
          };
        case 'targetId':
          if (dataItem.programName && dataItem.targetLabel) {
            return dataItem;
          }
          return {
            ...dataItem,
            programName: chain(this.programs).find({ id: dataItem.programId }).get('programName').value(),
            targetLabel: get(await this.getProgramTarget({
              program: dataItem.programId,
              target: dataItem.targetId,
              preventDuplicates: true,
            }), 'label', null)
              || `${dataItem.targetLabel} (${this.$t('common.deleted')})`
              || '',
            targetOrder: await this.getProgramTargetOrder(dataItem.programId, dataItem.targetId),
          };
        case 'programId':
          if (dataItem.programName) {
            return dataItem;
          }
          return {
            ...dataItem,
            programName: chain(this.programs).find({ id: dataItem.programId }).get('programName').value(),
          };
        case 'promptLevelId':
          if (dataItem.promptLevelLabel) {
            return dataItem;
          }
          return {
            ...dataItem,
            promptLevelLabel: this.getPromptLevelProp(
              dataItem.promptLevelTemplateId,
              dataItem.promptLevelId,
              'trialOutcome',
              '?',
            ),
            promptLevelOrder: this.getPromptLevelProp(
              dataItem.promptLevelTemplateId,
              dataItem.promptLevelId,
              'order',
              0,
            ),
          };
        case 'userId':
          return {
            ...dataItem,
            userName: this.getUserName(dataItem.userId),
          };
        default:
          return dataItem;
      }
    }, // getGroupingDataProperties

    getDivisor(dataItems, frequencyDivisor, frequencyDivisorDistinctBy) {
      if (!frequencyDivisorDistinctBy) {
        return sumBy(dataItems, frequencyDivisor);
      }

      const uniqDataItem = uniqBy(dataItems, frequencyDivisorDistinctBy);
      return sumBy(this.mapDivisorWithFrequencyField(uniqDataItem, frequencyDivisor, dataItems), frequencyDivisor);
    },

    mapDivisorWithFrequencyField(divisor, frequencyDivisor, dataItems) {
      const frequencyDivisorItem = (divisor.find((dataItem) => dataItem[frequencyDivisor]));
      if (frequencyDivisorItem || frequencyDivisorItem === 0) {
        return divisor;
      }

      const frequencyDuration = dataItems.find((dataItem) => dataItem[frequencyDivisor])?.[frequencyDivisor];
      if (!frequencyDuration) {
        return divisor;
      }

      return divisor.map((divisorItem, idx) => {
        if (idx !== 0) {
          return divisorItem;
        }

        return {
          ...divisorItem,
          [frequencyDivisor]: frequencyDuration,
        };
      });
    }, // mapDivisorWithFrequencyField

    async aggregateData(data, dataSeries, dataSeriesSettings) {
      const aggregatedDataActions = chain(data)
        .groupBy(dataSeriesSettings.xAxisAggregateBy)
        .map(async (dataItems, groupedIndex) => {
          let aggregatedSeriesData;

          if (dataSeriesSettings.aggregator === AGGREGATORS.FREQUENCY) {
            const frequencyData = chain(dataSeries)
            // @todo Refactor: Remove groupBy and just refer to series.seriesColumn for optimization
              .groupBy('seriesColumn')
              .map((series, seriesColumn) => {
                // Frequency is a bit tricky as it is based on durations for the session programs
                // We have to calculate individual frequencies and then get the average
                const {
                  frequencyScale, frequencyDivisor, frequencyDivisorDistinctBy,
                } = dataSeriesSettings;

                if (chain(dataItems)
                  .filter((dataItem) => has(dataItem, seriesColumn))
                  .isEmpty()
                  .value()) {
                  return {
                    [seriesColumn]: null,
                    [`${seriesColumn}_div`]: null,
                  };
                }
                const divident = chain(dataItems)
                  .sumBy(seriesColumn)
                  .value();

                const seriesId = chain(series).head().get('seriesId', null).value();
                const seriesProperty = chain(series).head().get('groupByDataProperty', null).value();

                let seriesItems = dataItems;

                // The seriesId is a string based on the data items' group by value
                // However, the actual group by value in data items can be null, so we need to convert it back
                if (seriesId !== null) {
                  const formattedSeriesId = seriesId === 'null' ? null : seriesId;
                  seriesItems = filter(dataItems, { [seriesProperty]: formattedSeriesId });
                }

                if (chain(seriesItems)
                  .reject((dataItem) => get(dataItem, frequencyDivisor, null) === null)
                  .isEmpty()
                  .value()) {
                  return {
                    [seriesColumn]: null,
                    [`${seriesColumn}_div`]: null,
                  };

                  // divisor = null;
                }
                const divisor = this.getDivisor(seriesItems, frequencyDivisor, frequencyDivisorDistinctBy);

                if (divisor === 0) {
                  return {
                    [seriesColumn]: null,
                    [`${seriesColumn}_div`]: null,
                  };
                }
                return {
                  [seriesColumn]: (divident * frequencyScale) / divisor,
                  [`${seriesColumn}_div`]: divisor,
                };
              })
              .value();
              // Flatten array of series info into single object
            aggregatedSeriesData = Object.assign(...frequencyData);
          } else if (dataSeriesSettings.aggregator === AGGREGATORS.WEIGHTED_AVERAGE) {
            const averageData = chain(dataSeries)
              .map((series) => {
                const { seriesColumn } = series;
                const val = chain(dataItems)
                  .map(seriesColumn)
                  .reject((v) => (v === null) || (v === undefined))
                  .mean()
                  .defaultTo(null)
                  .value();
                const weight = chain(dataItems)
                  .map(seriesColumn)
                  .reject((v) => (v === null) || (v === undefined))
                  .size()
                  .defaultTo(1)
                  .value();
                return {
                  [seriesColumn]: val,
                  [`${seriesColumn}_weight`]: weight,
                  trials: weight,
                };
              })
              .filter((item) => Object.values(item)?.[1])
              .value();
            aggregatedSeriesData = averageData.length ? Object.assign(...averageData) : {};
          } else {
            aggregatedSeriesData = chain(dataSeries)
              .keyBy('seriesColumn')
              .mapValues((dataSeriesItem) => {
                switch (dataSeriesSettings.aggregator) {
                  case 'count':
                    // Only undefined => return null
                    // Anything else: Count non-null values
                    return reduce(
                      dataItems,
                      (pV, dataItem) => (isUndefined(dataItem[dataSeriesItem.seriesColumn])
                        ? pV
                        : (pV || 0) + (isNumber(dataItem[dataSeriesItem.seriesColumn]) ? 1 : 0)
                      ),
                      null,
                    );
                  case 'percentage': {
                    const reducedValue = reduce(
                      dataItems,
                      (pV, dataItem) => (isUndefined(dataItem[dataSeriesItem.seriesColumn])
                        ? pV
                        : (pV || 0) + (isNumber(dataItem[dataSeriesItem.seriesColumn]) ? 1 : 0)
                      ),
                      null,
                    ) / reduce(
                      dataItems,
                      (pV, dataItem) => (isUndefined(dataItem[dataSeriesSettings.dataProperty])
                        ? pV
                        : (pV || 0) + (isNumber(dataItem[dataSeriesSettings.dataProperty]) ? 1 : 0)
                      ),
                      null,
                    );

                    return !Number.isNaN(reducedValue) ? reducedValue : null;
                  }
                  case 'sum':
                    // @todo reject(v=>get(v, dataSeriesItem.seriesColumn, null))
                    if (chain(dataItems)
                      .reject({ [dataSeriesItem.seriesColumn]: null })
                      .isEmpty()
                      .value()) {
                      return null;
                    }
                    return chain(dataItems)
                      .sumBy(dataSeriesItem.seriesColumn)
                      .value();
                  case 'average':
                    return chain(dataItems)
                      .map(dataSeriesItem.seriesColumn)
                      .reject((v) => (v === null) || (v === undefined))
                      .mean()
                      .defaultTo(null)
                      .value();
                  case 'low':
                    return chain(dataItems)
                      .minBy(dataSeriesItem.seriesColumn)
                      .get(dataSeriesItem.seriesColumn)
                      .value();
                  case 'high':
                    return chain(dataItems).maxBy(dataSeriesItem.seriesColumn).get(dataSeriesItem.seriesColumn).value();
                  case 'close':
                  default:
                    return chain(dataItems).last().get(dataSeriesItem.seriesColumn).value();
                }
              })
              .value();
          }
          const dataItem = dataItems.find((item) => item.targetLabel) || last(dataItems);

          const additionalFields = {};
          if (dataSeriesSettings?.dataSeriesPreset === 'spAverageRating') {
            additionalFields.trials = chain(dataItems)
              .map(dataSeries[0]?.seriesColumn)
              .reject((v) => (v === null) || (v === undefined))
              .size()
              .value();
          }

          return {
            ...await this.getGroupingDataProperties(
              dataSeriesSettings.xAxisDataProperty,
              dataItem,
              groupedIndex + 1,
            ),
            ...aggregatedSeriesData,
            ...additionalFields,
          };
        })
        .value();
      return sortBy(await Promise.all(aggregatedDataActions), dataSeriesSettings.xAxisOrderBy);
    }, // aggregateData()

    async addCategoryData(data, dataProperty, sort = null) {
      const aggregatedDataActions = map(
        data,
        async (dataItem, groupIndex) => ({
          ...dataItem,
          ...await this.getGroupingDataProperties(
            dataProperty,
            dataItem,
            groupIndex + 1,
          ),
        }),
      );
      if (!sort) {
        return Promise.all(aggregatedDataActions);
      }
      return sortBy(await Promise.all(aggregatedDataActions), sort);
    }, // addCategoryData()

    splitDataIntoPhaseIntervals(chartData, dataSeries) {
      return map(chartData, (dataItem) => {
        const dataItemsDate = get(dataItem, this.dataSeriesSettings.xAxisOrderBy);

        const phaseLineIndex = findIndex(this.phaseLineIntervals, (phaseLineInterval) => (
          (dataItemsDate >= phaseLineInterval.fromTimestamp)
                  && (dataItemsDate < phaseLineInterval.toTimestamp)
        ));
        if (phaseLineIndex === -1) {
          return {};
        }
        const phaseIntervalData = reduce(
          dataSeries,
          (acc, dataSeriesItem) => (
            {
              ...acc,
              ...(has(dataItem, dataSeriesItem.seriesColumn)
                  && { [`${dataSeriesItem.seriesColumn}_${phaseLineIndex}`]: dataItem[dataSeriesItem.seriesColumn] }),
              ...(has(dataItem, `${dataSeriesItem.seriesColumn}_div`)
                  && {
                    [`${`${dataSeriesItem.seriesColumn}_${phaseLineIndex}`}_div`]:
                      dataItem[`${dataSeriesItem.seriesColumn}_div`],
                  }),
              ...(has(dataItem, `${dataSeriesItem.seriesColumn}_weight`)
                  && {
                    [`${`${dataSeriesItem.seriesColumn}_${phaseLineIndex}`}_weight`]:
                      dataItem[`${dataSeriesItem.seriesColumn}_weight`],
                  }),
            }
          ),
          {},
        );
        return {
          ...dataItem,
          ...phaseIntervalData,
        };
      });
    },

    unzipChart(compressedChartData) {
      if (!compressedChartData) {
        return;
      }
      return new Promise((resolve) => {
        zlib.gunzip(Buffer.from(compressedChartData, 'base64'), (error, result) => {
          resolve(JSON.parse(result.toString()));
        });
      });
    }, // unzipChart()

    async getChart(dataSeriesSettings, dateRange, defaultPreloadFilter) {
      if (!this.clientId) {
        return { chartData: [], chartDataSeries: [] };
      }
      const preloadFilter = isEmpty(dataSeriesSettings.preloadFilter)
        ? defaultPreloadFilter
        : dataSeriesSettings.preloadFilter;
      const chartResult = await callFirebaseFunction('getChart', {
        clinicId: this.userDetails.clinic,
        clientId: this.clientId,
        dataSeriesSettings,
        dateRange,
        preloadFilter,
        compress: null,
      });

      const dateFields = get(chartResult, ['data', 'dateFields']);
      let chartData = [];

      if (get(chartResult, ['data', 'compressed'])) {
        chartData = await this.unzipChart(get(chartResult, ['data', 'compressedChartData']));
      } else {
        chartData = get(chartResult, ['data', 'chartData']);
      }

      if (!isEmpty(dateFields)) {
        // Convert dates back to Date objects (as JSON doesnt support native JS dates)
        chartData = map(chartData, (chartDataItem) => (
          Object.assign(chartDataItem, chain(dateFields)
            .map((dateStr) => [dateStr, chartDataItem[dateStr] ? new Date(chartDataItem[dateStr]) : null])
            .fromPairs()
            .value())
        ));
      }
      const chartDataSeries = get(chartResult, ['data', 'chartDataSeries'], []);

      const isAverageRating = this.dataSeriesSettings?.dataSeriesPreset === 'spAverageRating';
      chartData = chartData.filter((item) => !!item.isRatingScale === isAverageRating);

      return { chartData, chartDataSeries };
    }, // getChart()

    async convertFilters(dataSeriesSettings) {
      // @todo: Technically we could optimize the data fetch when the user filters
      // by target statuses.
      // This will require us to refactor the ChartConfigurator
      const postloadFilter = chain(dataSeriesSettings)
        .get('postloadFilter', {})
        .cloneDeep()
        .value();

      const preloadFilter = {};
      let programIdFilter = get(dataSeriesSettings, ['postloadFilter', 'programs', 'programId'], []);
      let targetIdFilter = get(dataSeriesSettings, ['postloadFilter', 'programs', 'targetId'], []);

      // Filter program types
      const filterProgramTypes = get(postloadFilter, ['programTypes', 'programType'], []);
      if (!isEmpty(filterProgramTypes)) {
        const programsWithSelectedTypes = chain(this.programs)
          .filter((program) => (filterProgramTypes.includes(program.type)))
          .map('id')
          .value();

        // Apply AND filter
        if (!isEmpty(programIdFilter)) {
          programIdFilter = intersection(programIdFilter, programsWithSelectedTypes);
        } else {
          programIdFilter = programsWithSelectedTypes;
        }
        delete postloadFilter.programTypes;
        set(postloadFilter, ['programs', 'programId'], programIdFilter);
      } // END: Filter program types

      // Filter program tags
      const filterProgramTags = get(postloadFilter, ['programTags', 'programTag'], []);
      if (!isEmpty(filterProgramTags)) {
        const programsWithSelectedTags = chain(this.programs)
          .filter((program) => (chain(filterProgramTags).difference(get(program, 'tags', [])).size().value() === 0))
          .map('id')
          .value();

        // Apply AND filter
        if (!isEmpty(programIdFilter)) {
          programIdFilter = intersection(programIdFilter, programsWithSelectedTags);
        } else {
          programIdFilter = programsWithSelectedTags;
        }
        delete postloadFilter.programTags;
        set(postloadFilter, ['programs', 'programId'], programIdFilter);
      } // END: Filter program tags

      // Spice up data with target status data if we have a filter on that
      const filterTargetStatuses = get(postloadFilter, ['targetStatuses', 'targetStatus']);
      if (!isEmpty(filterTargetStatuses)) {
        let programs;
        // If we have already limited to a specific number of programs there is no
        // need to load targets for all programs
        if (chain(postloadFilter).get(['programs', 'programId']).isArray().value()) {
          programs = get(postloadFilter, ['programs', 'programId']);
        } else {
          programs = map(this.programs, 'id');
        }

        const programTargetsActions = flatMap(
          programs,
          async (program) => {
            const dbTargets = await this.getAllProgramTargets({ program });

            if (this.initDraftProgramTargets.length && program === this.initProgramId) {
              return this.syncDBTargetsAndFrontendTargets(dbTargets);
            }

            return dbTargets;
          },
        );
        const allTargets = await Promise.all(programTargetsActions);
        const targetsWithSelectedStatus = chain(allTargets)
          .flatten()
          .filter((target) => (filterTargetStatuses.includes(target.status)))
          .map('id')
          .value();

        // Apply AND filter
        if (!isEmpty(targetIdFilter)) {
          targetIdFilter = intersection(targetIdFilter, targetsWithSelectedStatus);
        } else {
          targetIdFilter = targetsWithSelectedStatus;
        }
        delete postloadFilter.targetStatuses;
        set(postloadFilter, ['programs', 'targetId'], targetIdFilter);
      } // END: Filter target statuses

      if (chain(postloadFilter)
        .get(['programs', 'programId'], [])
        .size()
        .gt(0)
        .value()) {
        preloadFilter.programs = get(postloadFilter, ['programs', 'programId']);

        const filterTargetIds = get(postloadFilter, ['programs', 'targetId'], []);
        const filterParentTargetIds = get(postloadFilter, ['programs', 'parentTargetId'], []);
        if (filterTargetIds.length > 0 || filterParentTargetIds.length > 0) {
          const programsAndTargetActions = chain(postloadFilter)
            .get(['programs', 'programId'])
            .map(async (programId) => {
              const dbTargets = await this.getAllProgramTargets({ program: programId });

              if (this.initDraftProgramTargets.length && programId === this.initProgramId) {
                return {
                  programId,
                  programTargets: this.syncDBTargetsAndFrontendTargets(dbTargets),
                };
              }

              return {
                programId,
                programTargets: dbTargets,
              };
            })
            .value();
          const programsAndTargets = await Promise.all(programsAndTargetActions);

          // Make sure we only filter lowest level (eg. target or parent target) of selected
          // program filters
          const excludeIds = reduce(
            programsAndTargets,
            (cV, { programId, programTargets }) => {
              let programIds = [];
              if (!chain(filterTargetIds).intersection(map(programTargets, 'id')).isEmpty().value()
                || !chain(filterParentTargetIds).intersection(map(programTargets, 'parentTarget')).isEmpty().value()) {
                programIds = [programId];
              }
              const parentTargetIdsWithFilteredTargets = intersection(
                filterParentTargetIds,
                chain(programTargets)
                  .filter((programTarget) => includes(filterTargetIds, programTarget.id))
                  .map('parentTarget')
                  .value(),
              );
              return {
                programs: [...cV.programs, ...programIds],
                parentTargets: [...cV.parentTargets, ...parentTargetIdsWithFilteredTargets],
              };
            },
            {
              programs: [],
              parentTargets: [],
            },
          );

          postloadFilter.programs.programId = difference(
            postloadFilter.programs.programId,
            excludeIds.programs,
          );
          postloadFilter.programs.parentTargetId = difference(
            postloadFilter.programs.parentTargetId,
            excludeIds.parentTargets,
          );
        }
      }

      if (!chain(postloadFilter)
        .get(['topography', 'programBehaviourGroupId'], [])
        .flatMap((programBehaviourGroup) => chain(programBehaviourGroup)
          .get('programBehaviourItemId')
          .keys()
          .value())
        .isEmpty()
        .value()) {
        preloadFilter.topography = chain(postloadFilter)
          .get(['topography', 'programBehaviourGroupId'], [])
          .mapValues((programBehaviourGroup) => chain(programBehaviourGroup)
            .get('programBehaviourItemId')
            .keys()
            .value())
          .omitBy(isEmpty)
          .value();
        delete postloadFilter.topography;
      }
      if (chain(postloadFilter)
        .get(['assessmentTypes', 'assessmentId'], [])
        .size()
        .gt(0)
        .value()) {
        preloadFilter.assessments = get(postloadFilter, ['assessmentTypes', 'assessmentId']);
        delete postloadFilter.assessmentTypes.assessmentId;
      }

      const assessmentTypeDimensionFilters = chain(postloadFilter)
        .get('assessmentTypes', {})
        .keys()
        .filter((assessmentTypeDimensionKey) => (assessmentTypeDimensionKey.startsWith('dim_')))
        .value();

      if (assessmentTypeDimensionFilters.length > 0) {
        // Move dim_ properties out to AND filters (root of filters)
        assessmentTypeDimensionFilters.forEach((assessmentTypeDimensionFilter) => {
          set(
            postloadFilter,
            ['assessmentDimensions', assessmentTypeDimensionFilter],
            get(postloadFilter, ['assessmentTypes', assessmentTypeDimensionFilter]),
          );
          delete postloadFilter.assessmentTypes[assessmentTypeDimensionFilter];
        });
      }

      if (chain(postloadFilter)
        .get(['assessmentTypes', 'fieldId'], [])
        .size()
        .gt(0)
        .value()) {
        // Move metrics field (fieldId) out to AND filters (root of filters)
        postloadFilter.assessmentMetrics = {
          fieldId: get(postloadFilter, ['assessmentTypes', 'fieldId']),
        };
        delete postloadFilter.assessmentTypes.fieldId;
      }

      return {
        preloadFilter,
        postloadFilter,
      };
    }, // convertFilters()

    /**
     * Refresh graph data by reloading and filtering it from the database
     */
    async refreshData() {
      if (!this.canRender) {
        return;
      }
      this.isLoading = true;

      // Prevent having to reload programs and targets on back-end
      const { preloadFilter, postloadFilter } = await this.convertFilters(this.dataSeriesSettings);
      const dataSeriesSettingsForChart = {
        ...this.dataSeriesSettings,
        postloadFilter,
      };

      const { chartData } = await this.getChart(dataSeriesSettingsForChart, this.dateRange, preloadFilter);
      this.rawChartData = chartData;

      // Load status change phase lines from the program's statusChanges
      const programIds = get(preloadFilter, 'programs', []);
      this.loadStatusPhaseLines(programIds);
    }, // refreshData()

    async refreshChart() {
      if (!this.canRender) {
        return;
      }
      let chartData = this.rawChartData;

      if (this.dataSeriesSettings.additionalSettings?.hideDeletedTargets) {
        chartData = await this.filterOutDeletedTargetsData(chartData);
      }

      if ((get(this.dataSeriesSettings, 'groupByDataProperty') === 'userId')
         || (get(this.dataSeriesSettings, 'xAxisDataProperty') === 'userId')) {
        await this.reloadClientUsers();
      }

      let chartDataSeries = await this.getDataSeries(
        chartData,
        this.dataSeriesSettings,
      );
      // Aggregate data for x-axis if neeeded
      if (this.dataSeriesSettings.xAxisAggregateBy) {
        chartData = await this.aggregateData(
          chartData,
          chartDataSeries,
          this.dataSeriesSettings,
        );
      } else if (this.dataSeriesSettings.xAxisDataProperty) {
        chartData = await this.addCategoryData(
          chartData,
          this.dataSeriesSettings.xAxisDataProperty,
          this.dataSeriesSettings.xAxisOrderBy,
        );
      }
      if (this.dataSeriesSettings.axisCategory) {
        chartData = await this.addCategoryData(
          chartData,
          this.dataSeriesSettings.axisCategory,
        );
      }

      // Settle this.chartData before the split: splitDataIntoPhaseIntervals buckets by
      // phaseLineIntervals, derived from this.chartData — a stale value orphans points.
      this.chartData = chartData;

      if (this.usePhaseLineIntervals) {
        chartData = this.splitDataIntoPhaseIntervals(
          chartData,
          chartDataSeries,
        );
      }
      chartDataSeries = orderBy(chartDataSeries, 'order');

      const exportColumnObjects = await this.getTableColumns(
        this.getCurrentDataSource().tableColumns,
        chartDataSeries,
        this.dataSeriesSettings,
        true,
        chartData,
      );

      this.exportColumns = chain(exportColumnObjects)
        .keyBy('field')
        .mapValues(
          (exportColumn) => (exportColumn.label),
        )
        .value();

      this.exportFormatters = chain(exportColumnObjects)
        .keyBy('field')
        .mapValues(
          (exportColumn) => (exportColumn.exportFormat),
        )
        .omitBy((exportFormatter) => !isFunction(exportFormatter))
        .value();

      this.chartDataSeries = chartDataSeries;

      this.chartData = chartData;

      // Also add it to the data table
      this.tableDataSeriesSettings = this.dataSeriesSettings;
    }, // refreshChart()

    async filterOutDeletedTargetsData(chartData) {
      const allTargetsData = chain(chartData)
        .map(({ targetId, programId }) => ({ targetId, programId }))
        .uniqBy(({ targetId, programId }) => `${targetId}_${programId}`)
        .value();
      const existingTargetsData = await Promise.all(
        allTargetsData.map(async ({ targetId, programId }) => {
          const target = await this.getProgramTarget({
            program: programId,
            target: targetId,
          });
          return (target && target.createTimestamp) ? null : targetId;
        }),
      );
      const deletedTargetsIds = existingTargetsData.filter((target) => !!(target));

      // group chartData by targetsIds and filter those that are related to deleted targets (better performance)
      return chain(chartData)
        .groupBy('targetId')
        .values()
        .filter(([firstChartDataByTargetId]) => {
          return !deletedTargetsIds.includes(firstChartDataByTargetId.targetId);
        })
        .flatten()
        .value();
    }, // filterOutDeletedTargetsData()

    getUserName(userId) {
      return get(this.userNameCache, userId, '?');
    }, // getUserName()

    getModeLabel(programType, mode) {
      switch (programType) {
        case 'sbt': {
          return get(this.$tm('run.collectData.sbtModes'), mode, '');
        }
        case 'set': {
          return get(this.$tm('run.collectData.setModes'), mode, '');
        }
        case 'ptp': {
          return get(this.$tm('run.collectData.probeTeachProbeModes'), mode, '');
        }
        default: {
          return '';
        }
      }
    }, // getModeLabel()

    getModeOrder(programType, mode) {
      switch (programType) {
        case 'sbt': {
          return indexOf(this.$tm('run.collectData.sbtModes'), mode) || 0;
        }
        case 'set': {
          return indexOf(this.$tm('run.collectData.setModes'), mode) || 0;
        }
        case 'ptp': {
          return indexOf(this.$tm('run.collectData.probeTeachProbeModes'), mode) || 0;
        }
        default: {
          return 0;
        }
      }
    }, // getModeOrder()

    getPromptLevelProp(promptLevelTemplateId, promptLevelId, prop, defaultValue) {
      return chain(this.promptLevelTemplates)
        .get([promptLevelTemplateId, 'promptLevels'])
        .find({ id: promptLevelId })
        .get(prop, defaultValue)
        .value();
    },

    async reloadChartPhaseLineScopes(phaseLineScopes, refreshAll = false, forceRefresh = false) {
      if (!this.clientId) {
        return;
      }

      let refreshPhaseLineScopes = [];

      if (forceRefresh) {
        refreshPhaseLineScopes = reject(
          phaseLineScopes,
          (phaseLineScope) => (get(phaseLineScope, 'scope', 'local') === 'local'),
        );
      } else {
        refreshPhaseLineScopes = chain(phaseLineScopes)
          .reject((phaseLineScope) => {
            if (get(phaseLineScope, 'scope', 'local') === 'local') {
              return true;
            }
            // Don't reload scopes that are already loaded
            const scopeFilter = this.getPhaseLineScopeFilter(
              phaseLineScope.scope,
              phaseLineScope.program,
              phaseLineScope.target,
            );
            return chain(this.chartPhaseLineScopes)
              .find(scopeFilter)
              .get('isLoaded', false)
              .value();
          })
          .value();
      }
      if (!isEmpty(refreshPhaseLineScopes)) {
        this.isLoadingPhaseLines = true;

        const loadChartPhaseLineActions = map(
          refreshPhaseLineScopes,
          async (phaseLine) => {
            const chartPhaseLinesDb = new ChartPhaseLinesDB();
            const chartPhaseLines = await chartPhaseLinesDb.readPhaseLineScope(
              this.userDetails.clinic,
              this.clientId,
              phaseLine,
            );
            return {
              ...phaseLine,
              chartPhaseLines,
              isLoaded: true,
            };
          },
        );
        const refreshedPhaseLineScopes = await Promise.all(loadChartPhaseLineActions);
        if (refreshAll) {
          this.chartPhaseLineScopes = refreshedPhaseLineScopes;
        } else {
          this.chartPhaseLineScopes = unionWith(
            refreshedPhaseLineScopes,
            this.chartPhaseLineScopes,
            (phaseLineScopeA, phaseLineScopeB) => isEqual(
              this.getPhaseLineScopeFilter(
                phaseLineScopeA.scope,
                phaseLineScopeA.program || null,
                phaseLineScopeA.target || null,
              ),
              this.getPhaseLineScopeFilter(
                phaseLineScopeB.scope,
                phaseLineScopeB.program || null,
                phaseLineScopeB.target || null,
              ),
            ),
          );
        }
        this.isLoadingPhaseLines = false;
      }
    }, // reloadChartPhaseLines()

    loadStatusPhaseLines(programIds) {
      // Status transitions are program-specific. With more than one program on the chart
      // they would render as global axis boundaries affecting every series, splitting the
      // per-phase averages/trendlines at dates that don't correspond to each series' own
      // program transitions. Restrict the feature to single-program charts.
      if (programIds.length !== 1) {
        this.statusPhaseLines = [];
        return;
      }

      const statusLabels = {
        baseline: this.$t('programStatus_baseline'),
        active: this.$t('programStatus_active'),
        maintenance: this.$t('programStatus_maintenance'),
      };

      // A program follows a single monotonic path (baseline → active → maintenance), and
      // statusChanges is a map keyed by status, so each status is recorded at most once.
      // Read the transition timestamps straight off the program instead of replaying the
      // activities log — no cycling or duplicate transitions to reconcile.
      const program = chain(this.programs).find({ id: programIds[0] }).value();
      const statusChanges = get(program, 'statusChanges', {});

      // Visible statuses that actually have a recorded transition, ordered by when the
      // program entered them.
      const transitions = Object.keys(statusLabels)
        .filter((status) => chain(statusChanges).get([status, 'toDate']).isFunction().value())
        .map((status) => ({ status, timestamp: statusChanges[status] }))
        .sort((a, b) => a.timestamp.toDate() - b.timestamp.toDate());

      // The earliest status is the section the chart opens in (its initialStatusLabel);
      // every later status is a transition line. With fewer than two statuses there is no
      // transition to draw, so no phase lines.
      const phaseLines = transitions.slice(1).map(({ status, timestamp }) => ({
        startTimestamp: timestamp,
        label: statusLabels[status],
        isStatusTransition: true,
      }));
      if (phaseLines.length > 0) {
        phaseLines[0].initialStatusLabel = statusLabels[transitions[0].status];
      }

      this.statusPhaseLines = phaseLines;
    }, // loadStatusPhaseLines()

    /**
     * on the editProgram page, you can modify target data or create a new target, and until saved, these changes are
     * stored only on the frontend. To ensure the graph always has up-to-date targets data for the current program,
     * it is necessary to synchronize the data received from the database with the data stored on the frontend.
     */
    syncDBTargetsAndFrontendTargets(dbTargets) {
      const initTargetsMap = keyBy(this.initDraftProgramTargets, 'id');

      const updatedTargets = dbTargets.map((dbTarget) => {
        const target = initTargetsMap[dbTarget.id];
        return target ? { ...dbTarget, ...target } : dbTarget;
      });

      const newTargets = this.initDraftProgramTargets.filter((target) => {
        return get(target, 'editStatus', '') === 'create';
      });

      return [...updatedTargets, ...newTargets];
    },

    /**
     * Retrieves unique prompt level template IDs directly from the raw chart data
     * @returns {Array<string>}
     */
    getActiveRatingTemplateIds() {
      if (!Array.isArray(this.rawChartData) || this.rawChartData.length === 0) {
        return [];
      }

      const ids = this.rawChartData
        .map((item) => item?.promptLevelTemplateId || item?.promptLevelTemplate)
        .filter(Boolean);

      return [...new Set(ids)];
    }, // getActiveRatingTemplateIds()

    /**
     * Calculates custom minimum and maximum score values across rating scale prompt level templates
     * @param {Array<string>} templateIds
     * @returns {{min: number, max: number}}
     */
    getRatingScaleMinMax(templateIds) {
      const allScores = [];

      templateIds.forEach((templateId) => {
        const template = this.promptLevelTemplates?.[templateId];

        if (template && !template.isDeleted) {
          const levels = template.promptLevels || [];

          levels.forEach((l) => {
            if (typeof l.score === 'number' && !l.isDeleted) {
              allScores.push(l.score);
            }
          });
        }
      });

      if (allScores.length === 0 && this.rawChartData?.length > 0) {
        const dataScores = this.rawChartData
          .map((item) => item?.scoreValue)
          .filter((v) => typeof v === 'number');

        if (dataScores.length > 0) {
          allScores.push(...dataScores);
        }
      }

      let min = allScores.length > 0 ? Math.min(...allScores) : 1;
      let max = allScores.length > 0 ? Math.max(...allScores) : 10;

      if (min === max) {
        min = Math.max(0, min - 1);
        max += 1;
      }

      return { min, max };
    }, // getRatingScaleMinMax()

  },

};
</script>
<style scoped>
.chart-container {
  height: 410px;
}
.chart-container--with-status-headers {
  height: 435px;
}
</style>
